import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Company } from '../../types/company';
import toast from 'react-hot-toast';
import { Badge } from '../../components/ui';

interface WizardCompanyForm {
    name: string;
    billingEmail: string;
    document: string;
    autoApprove: boolean;
}

interface SearchedUser {
    id: string;
    name: string;
    email: string;
    companyId: string | null;
    companyName?: string | null;
}

interface SelectedMember {
    id: string;
    name: string;
    email: string;
    role: 'admin' | 'member';
    isOwner: boolean;
}

const initialWizardForm: WizardCompanyForm = {
    name: '',
    billingEmail: '',
    document: '',
    autoApprove: false,
};

const STEP_LABELS = ['Dados da Organização', 'Selecionar Membros', 'Revisão'];

const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => (
    <div className="flex items-center justify-center mb-8">
        {STEP_LABELS.map((label, idx) => {
            const stepNum = idx + 1;
            const isActive = stepNum === currentStep;
            const isCompleted = stepNum < currentStep;
            return (
                <React.Fragment key={stepNum}>
                    {idx > 0 && (
                        <div className={`w-12 h-0.5 ${isCompleted ? 'bg-snapfy-primary' : 'bg-snapfy-inner'}`} />
                    )}
                    <div className="flex flex-col items-center">
                        <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                                isActive
                                    ? 'border-snapfy-primary bg-snapfy-primary text-white'
                                    : isCompleted
                                      ? 'border-snapfy-primary bg-snapfy-primary/20 text-snapfy-accent-soft'
                                      : 'border-snapfy-border bg-snapfy-surface-hover text-snapfy-text-secondary'
                            }`}
                        >
                            {isCompleted ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            ) : (
                                stepNum
                            )}
                        </div>
                        <span className={`text-xs mt-1 whitespace-nowrap ${isActive ? 'text-snapfy-accent-soft' : 'text-snapfy-text-secondary'}`}>
                            {label}
                        </span>
                    </div>
                </React.Fragment>
            );
        })}
    </div>
);

const CreateCompanyWizard: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}> = ({ isOpen, onClose, onCreated }) => {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState<WizardCompanyForm>(initialWizardForm);
    const [showFiscal, setShowFiscal] = useState(false);

    // Step 2 state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);
    const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Step 3 state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset all state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setForm(initialWizardForm);
            setShowFiscal(false);
            setSearchQuery('');
            setSearchResults([]);
            setSelectedMembers([]);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    // Debounced user search
    useEffect(() => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }
        searchDebounce.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await api.get(`/admin/users?search=${encodeURIComponent(searchQuery)}&per_page=20`);
                const users = (response.data.data || []).map((u: Record<string, unknown>) => ({
                    id: u.id as string,
                    name: u.name as string,
                    email: u.email as string,
                    companyId: (u.companyId as string) || null,
                    companyName: (u.companyName as string) || null,
                }));
                setSearchResults(users);
            } catch {
                toast.error('Erro ao buscar usuários.');
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
        return () => {
            if (searchDebounce.current) clearTimeout(searchDebounce.current);
        };
    }, [searchQuery]);

    const isUserSelected = (userId: string) => selectedMembers.some((m) => m.id === userId);

    const handleSelectUser = (user: SearchedUser) => {
        if (user.companyId || isUserSelected(user.id)) return;
        const isFirst = selectedMembers.length === 0;
        setSelectedMembers((prev) => [
            ...prev,
            { id: user.id, name: user.name, email: user.email, role: isFirst ? 'admin' : 'member', isOwner: isFirst },
        ]);
    };

    const handleRemoveMember = (userId: string) => {
        setSelectedMembers((prev) => {
            const filtered = prev.filter((m) => m.id !== userId);
            // If removed member was owner, reassign to first member
            if (filtered.length > 0 && !filtered.some((m) => m.isOwner)) {
                filtered[0].isOwner = true;
                filtered[0].role = 'admin';
            }
            return [...filtered];
        });
    };

    const handleSetOwner = (userId: string) => {
        setSelectedMembers((prev) =>
            prev.map((m) => ({
                ...m,
                isOwner: m.id === userId,
                role: m.id === userId ? 'admin' : m.role,
            })),
        );
    };

    const handleChangeRole = (userId: string, role: 'admin' | 'member') => {
        setSelectedMembers((prev) =>
            prev.map((m) => (m.id === userId ? { ...m, role, isOwner: role === 'member' ? false : m.isOwner } : m)),
        );
    };

    const owner = selectedMembers.find((m) => m.isOwner);

    // Step navigation
    const canProceedStep1 = form.name.trim() && form.billingEmail.trim();
    const canProceedStep2 = selectedMembers.length > 0 && selectedMembers.some((m) => m.isOwner);

    const handleNext = () => {
        if (step === 1 && !canProceedStep1) {
            toast.error('Nome e email são obrigatórios.');
            return;
        }
        if (step === 2 && !canProceedStep2) {
            toast.error('Selecione pelo menos 1 membro (owner).');
            return;
        }
        setStep((s) => Math.min(s + 1, 3));
    };

    const handleBack = () => setStep((s) => Math.max(s - 1, 1));

    const handleSubmit = async () => {
        if (!owner) return;
        setIsSubmitting(true);
        try {
            const createRes = await api.post('/admin/companies', {
                name: form.name,
                billingEmail: form.billingEmail,
                document: form.document || undefined,
                autoApprove: form.autoApprove,
                ownerEmail: owner.email,
            });
            const companyId = createRes.data.data?.id || createRes.data.id;

            // Add additional members (not the owner)
            const additionalMembers = selectedMembers.filter((m) => !m.isOwner);
            const memberErrors: string[] = [];
            for (const member of additionalMembers) {
                try {
                    await api.post(`/admin/companies/${companyId}/users`, {
                        userId: member.id,
                        role: member.role,
                    });
                } catch {
                    memberErrors.push(member.email);
                }
            }

            if (memberErrors.length > 0) {
                toast.success(
                    `Empresa criada! Falha ao adicionar: ${memberErrors.join(', ')}`,
                    { duration: 5000 },
                );
            } else {
                toast.success(
                    `Empresa "${form.name}" criada com ${selectedMembers.length} membro(s)!`,
                );
            }
            onCreated();
            onClose();
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } } };
            toast.error(err?.response?.data?.message || 'Erro ao criar empresa.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const modalWidth = step === 2 ? 'max-w-3xl' : step === 3 ? 'max-w-2xl' : 'max-w-md';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className={`bg-snapfy-surface rounded-xl border border-snapfy-border w-full ${modalWidth} mx-4 p-6 max-h-[90vh] overflow-y-auto transition-all`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-snapfy-text-primary">Nova Empresa</h2>
                    <button onClick={onClose} className="text-snapfy-text-secondary hover:text-snapfy-text-primary">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <StepIndicator currentStep={step} />

                {/* Step 1: Company Data */}
                {step === 1 && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Nome <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-lg px-3 py-2 text-snapfy-text-primary text-sm focus:outline-none focus:border-snapfy-primary"
                                placeholder="Nome da empresa"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-snapfy-text-secondary mb-1">
                                Email <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="email"
                                value={form.billingEmail}
                                onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
                                className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-lg px-3 py-2 text-snapfy-text-primary text-sm focus:outline-none focus:border-snapfy-primary"
                                placeholder="email@empresa.com"
                            />
                        </div>

                        {/* Collapsible fiscal data */}
                        <div className="border border-snapfy-border rounded-lg overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setShowFiscal(!showFiscal)}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm text-snapfy-text-secondary hover:bg-snapfy-surface-hover transition-colors"
                            >
                                <span>Dados Fiscais (opcional)</span>
                                <svg
                                    className={`w-4 h-4 transition-transform ${showFiscal ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showFiscal && (
                                <div className="px-3 pb-3">
                                    <label className="block text-sm font-medium text-snapfy-text-secondary mb-1">CNPJ</label>
                                    <input
                                        type="text"
                                        value={form.document}
                                        onChange={(e) => setForm({ ...form, document: e.target.value })}
                                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-lg px-3 py-2 text-snapfy-text-primary text-sm focus:outline-none focus:border-snapfy-primary"
                                        placeholder="00.000.000/0000-00"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="wizardAutoApprove"
                                checked={form.autoApprove}
                                onChange={(e) => setForm({ ...form, autoApprove: e.target.checked })}
                                className="w-4 h-4 rounded border-snapfy-border bg-snapfy-surface-hover text-snapfy-primary focus:ring-snapfy-focus"
                            />
                            <label htmlFor="wizardAutoApprove" className="text-sm text-snapfy-text-secondary">
                                Aprovar automaticamente
                            </label>
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleNext}
                                className="px-4 py-2 text-sm bg-snapfy-primary hover:bg-[#5a4dd4] text-white rounded-lg transition-colors"
                            >
                                Próximo →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Select Members */}
                {step === 2 && (
                    <div className="space-y-4">
                        {/* Search */}
                        <div>
                            <label className="block text-sm font-medium text-snapfy-text-secondary mb-1">Buscar usuários</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-lg pl-9 pr-3 py-2 text-snapfy-text-primary text-sm placeholder-snapfy-text-secondary focus:outline-none focus:border-snapfy-primary"
                                    placeholder="Buscar por nome ou email..."
                                />
                                <svg
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-snapfy-text-secondary"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                {isSearching && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <div className="w-4 h-4 border-2 border-snapfy-primary border-t-transparent rounded-full animate-spin" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Search Results */}
                        {searchResults.length > 0 && (
                            <div className="border border-snapfy-border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                                {searchResults.map((user) => {
                                    const hasCompany = !!user.companyId;
                                    const selected = isUserSelected(user.id);
                                    return (
                                        <button
                                            key={user.id}
                                            type="button"
                                            disabled={hasCompany || selected}
                                            onClick={() => handleSelectUser(user)}
                                            className={`w-full flex items-center justify-between px-3 py-2 text-sm border-b border-snapfy-border last:border-b-0 transition-colors ${
                                                hasCompany
                                                    ? 'opacity-50 cursor-not-allowed bg-snapfy-inner'
                                                    : selected
                                                      ? 'bg-snapfy-primary/10 cursor-default'
                                                      : 'hover:bg-snapfy-surface-hover cursor-pointer'
                                            }`}
                                        >
                                            <div className="text-left">
                                                <div className={`font-medium ${hasCompany ? 'text-snapfy-text-secondary' : 'text-snapfy-text-primary'}`}>
                                                    {user.name}
                                                </div>
                                                <div className={`text-xs ${hasCompany ? 'text-snapfy-text-secondary' : 'text-snapfy-text-secondary'}`}>
                                                    {user.email}
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0 ml-2">
                                                {hasCompany ? (
                                                    <span className="text-xs text-snapfy-text-secondary italic">(já em empresa)</span>
                                                ) : selected ? (
                                                    <span className="text-xs text-snapfy-accent-soft">Selecionado</span>
                                                ) : (
                                                    <span className="text-xs text-snapfy-text-secondary">+ Adicionar</span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {searchQuery.trim() && !isSearching && searchResults.length === 0 && (
                            <p className="text-sm text-snapfy-text-secondary text-center py-2">Nenhum usuário encontrado.</p>
                        )}

                        {/* Selected Members */}
                        {selectedMembers.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-snapfy-text-secondary mb-2">
                                    Membros selecionados ({selectedMembers.length})
                                </h3>
                                <div className="border border-snapfy-border rounded-lg divide-y divide-snapfy-border">
                                    {selectedMembers.map((member) => (
                                        <div key={member.id} className="flex items-center gap-3 px-3 py-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-snapfy-text-primary truncate">
                                                    {member.name}
                                                    {member.isOwner && (
                                                        <span className="ml-2 text-xs text-yellow-400 font-normal">(Owner)</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-snapfy-text-secondary truncate">{member.email}</div>
                                            </div>
                                            <select
                                                value={member.role}
                                                onChange={(e) => handleChangeRole(member.id, e.target.value as 'admin' | 'member')}
                                                className="bg-snapfy-surface-hover border border-snapfy-border rounded px-2 py-1 text-xs text-snapfy-text-primary focus:outline-none focus:border-snapfy-primary"
                                            >
                                                <option value="admin">Admin</option>
                                                <option value="member">Member</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => handleSetOwner(member.id)}
                                                title="Definir como owner"
                                                className={`p-1 rounded transition-colors ${
                                                    member.isOwner
                                                        ? 'text-yellow-400'
                                                        : 'text-snapfy-text-secondary hover:text-yellow-400'
                                                }`}
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M10 2l2.5 5.5L18 8.5l-4 4 1 5.5L10 15.5 4.5 18l1-5.5-4-4 5.5-1z" />
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveMember(member.id)}
                                                title="Remover membro"
                                                className="p-1 text-snapfy-text-secondary hover:text-red-400 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-4 py-2 text-sm text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                            >
                                ← Voltar
                            </button>
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={!canProceedStep2}
                                className="px-4 py-2 text-sm bg-snapfy-primary hover:bg-[#5a4dd4] text-white rounded-lg disabled:opacity-50 transition-colors"
                            >
                                Próximo →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Review */}
                {step === 3 && (
                    <div className="space-y-4">
                        <div className="bg-snapfy-surface-hover border border-snapfy-border rounded-lg p-4 space-y-3">
                            <div>
                                <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider">Empresa</span>
                                <p className="text-sm text-snapfy-text-primary font-medium">{form.name}</p>
                            </div>
                            <div>
                                <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider">Email</span>
                                <p className="text-sm text-snapfy-text-primary">{form.billingEmail}</p>
                            </div>
                            {form.document && (
                                <div>
                                    <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider">CNPJ</span>
                                    <p className="text-sm text-snapfy-text-primary">{form.document}</p>
                                </div>
                            )}
                            <div>
                                <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider">Aprovação automática</span>
                                <p className="text-sm text-snapfy-text-primary">{form.autoApprove ? 'Sim' : 'Não'}</p>
                            </div>
                        </div>

                        <div className="bg-snapfy-surface-hover border border-snapfy-border rounded-lg p-4 space-y-3">
                            {owner && (
                                <div>
                                    <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider">Owner</span>
                                    <p className="text-sm text-snapfy-text-primary">
                                        {owner.name} <span className="text-snapfy-text-secondary">({owner.email})</span>
                                    </p>
                                </div>
                            )}
                            <div>
                                <span className="text-xs text-snapfy-text-secondary uppercase tracking-wider">
                                    Membros ({selectedMembers.length})
                                </span>
                                <div className="mt-1 space-y-1">
                                    {selectedMembers.map((m) => (
                                        <div key={m.id} className="flex items-center justify-between text-sm">
                                            <span className="text-snapfy-text-primary">
                                                {m.name} <span className="text-snapfy-text-secondary">({m.email})</span>
                                            </span>
                                            <span className="text-xs text-snapfy-text-secondary capitalize">
                                                {m.isOwner ? 'owner' : m.role}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-4 py-2 text-sm text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                            >
                                ← Voltar
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm bg-snapfy-primary hover:bg-[#5a4dd4] text-white rounded-lg disabled:opacity-50 transition-colors"
                            >
                                {isSubmitting ? 'Criando...' : 'Criar Empresa'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdminCompaniesPage: React.FC = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [planFilter, setPlanFilter] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCompanies = useCallback(async (status: string, plan: string, search: string) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.append('status', status);
            if (plan) params.append('plan', plan);
            if (search) params.append('search', search);
            const query = params.toString();
            const response = await api.get(`/admin/companies${query ? `?${query}` : ''}`);
            setCompanies(response.data.data);
        } catch (error) {
            console.error('Failed to fetch companies', error);
            toast.error('Erro ao carregar empresas.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Re-fetch when status or plan filters change (immediate)
    useEffect(() => {
        fetchCompanies(statusFilter, planFilter, searchFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, planFilter]);

    // Debounced search
    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            fetchCompanies(statusFilter, planFilter, searchFilter);
        }, 300);

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchFilter]);

    const handleApprove = async (id: string) => {
        try {
            await api.post(`/admin/companies/${id}/approve`);
            toast.success('Empresa aprovada!');
            fetchCompanies(statusFilter, planFilter, searchFilter);
        } catch (error) {
            toast.error('Falha ao aprovar empresa.');
        }
    };

    const statusLabel = (status: string) => {
        switch (status) {
            case 'approved': return 'Aprovada';
            case 'pending': return 'Pendente';
            case 'rejected': return 'Rejeitada';
            default: return status;
        }
    };

    const selectClasses = 'bg-snapfy-surface-hover border border-snapfy-border rounded-lg px-3 py-2 text-sm text-snapfy-text-primary focus:outline-none focus:border-snapfy-primary';

    return (
        <div className="container mx-auto px-4 py-8 text-snapfy-text-primary">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold">Gestão de Empresas</h1>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 bg-snapfy-primary hover:bg-[#5a4dd4] text-white text-sm font-medium rounded-lg transition-colors"
                >
                    + Nova Empresa
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={selectClasses}
                >
                    <option value="">Status: Todos</option>
                    <option value="approved">Aprovada</option>
                    <option value="pending">Pendente</option>
                    <option value="rejected">Rejeitada</option>
                </select>

                <select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    className={selectClasses}
                >
                    <option value="">Plano: Todos</option>
                    <option value="free">Free</option>
                    <option value="paid">Pago</option>
                </select>

                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <input
                        type="text"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        placeholder="Buscar por nome ou email..."
                        className="w-full bg-snapfy-surface-hover border border-snapfy-border rounded-lg pl-9 pr-3 py-2 text-sm text-snapfy-text-primary placeholder-snapfy-text-secondary focus:outline-none focus:border-snapfy-primary"
                    />
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-snapfy-text-secondary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
            </div>

            {isLoading ? (
                <div>Carregando...</div>
            ) : (
                <div className="bg-snapfy-surface rounded-xl border border-snapfy-border overflow-hidden">
                    <table className="min-w-full divide-y divide-snapfy-border">
                        <thead className="bg-snapfy-inner">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase tracking-wider">Empresa</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase tracking-wider">Email</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase tracking-wider">Plano</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase tracking-wider">Créditos</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-snapfy-surface divide-y divide-snapfy-border">
                            {companies.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-snapfy-text-secondary text-sm">
                                        Nenhuma empresa encontrada.
                                    </td>
                                </tr>
                            ) : (
                                companies.map((company) => (
                                    <tr key={company.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-snapfy-text-primary">{company.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-snapfy-text-secondary">{company.billingEmail}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`text-sm ${company.activeSubscription?.plan?.name ? 'text-snapfy-accent-soft' : 'text-snapfy-text-secondary'}`}>
                                                {company.activeSubscription?.plan?.name || 'Sem plano'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant={
                                                company.status === 'approved' ? 'success' :
                                                company.status === 'pending' ? 'warning' : 'error'
                                            }>
                                                {statusLabel(company.status)}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-snapfy-text-secondary">
                                            {company.creditsBalance}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <Link to={`/admin/companies/${company.id}`} className="text-snapfy-accent-soft hover:text-snapfy-accent-soft mr-4">Ver Detalhes</Link>
                                            {company.status === 'pending' && (
                                                <button onClick={() => handleApprove(company.id)} className="text-green-400 hover:text-green-300">Aprovar</button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateCompanyWizard
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => fetchCompanies(statusFilter, planFilter, searchFilter)}
            />
        </div>
    );
};

export default AdminCompaniesPage;
